const { createAdAuctionCoordinator } = require('./adAuctionCoordinator');
const { createAdDiagnostics } = require('./adDiagnostics');
const { getSlotTargeting } = require('./gptConfig');
const {
  recordStudyPageview,
  recordStudySlotRender,
  recordStudySlotViewable
} = require('./insights/study');

const DEFAULT_AUCTION_STRATEGY_BY_BRAND = Object.freeze({
  narcity: 'coordinated',
  mtlblog: 'coordinated'
});

function resolveAuctionStrategy({ brand, userConfig = {}, runtimeWindow } = {}) {
  const resolvedWindow = runtimeWindow || (typeof window !== 'undefined' ? window : null);
  const explicit = userConfig.auctionStrategy;
  if (explicit === 'coordinated' || explicit === 'legacy') {
    return explicit;
  }

  const globalStrategy = resolvedWindow && resolvedWindow._millaAdAuctionStrategy;
  if (globalStrategy && typeof globalStrategy === 'object') {
    const brandStrategy = globalStrategy[brand];
    if (brandStrategy === 'coordinated' || brandStrategy === 'legacy') {
      return brandStrategy;
    }
  }
  if (globalStrategy === 'coordinated' || globalStrategy === 'legacy') {
    return globalStrategy;
  }

  return DEFAULT_AUCTION_STRATEGY_BY_BRAND[brand] || 'legacy';
}

function createPageAdRuntime(options = {}) {
  const runtimeWindow = options.runtimeWindow
    || (typeof window !== 'undefined' ? window : null);
  const diagnostics = createAdDiagnostics({
    enabled: options.diagnosticsEnabled === true,
    maxEvents: options.maxDiagnosticEvents,
    runtimeWindow,
    context: {
      brand: options.brand,
      pageType: options.pageType,
      device: options.device,
      pageKey: options.pageKey
    }
  });
  const coordinated = options.auctionStrategy === 'coordinated';
  const coordinator = coordinated
    ? createAdAuctionCoordinator({
      pbjs: options.pbjs,
      googletag: options.googletag,
      diagnostics,
      timeoutMs: options.timeoutMs,
      runtimeDocument: options.runtimeDocument
    })
    : null;
  let pubads = null;
  let slotRenderHandler = null;
  let impressionViewableHandler = null;
  const prebidHandlers = [];
  const cleanupHandlers = [];
  let destroyed = false;

  const runtime = {
    auctionStrategy: coordinated ? 'coordinated' : 'legacy',
    coordinated,
    coordinator,
    diagnostics,
    addCleanup(handler) {
      if (typeof handler === 'function' && !destroyed) {
        cleanupHandlers.push(handler);
      }
    },
    observeGpt(resolvedPubads) {
      if (!resolvedPubads || typeof resolvedPubads.addEventListener !== 'function' || slotRenderHandler) {
        return;
      }
      pubads = resolvedPubads;
      slotRenderHandler = (event) => {
        const slot = event && event.slot;
        const slotId = slot && typeof slot.getSlotElementId === 'function'
          ? slot.getSlotElementId()
          : '';
        diagnostics.record('gam_slot_rendered', {
          slotId,
          isEmpty: !!(event && event.isEmpty),
          size: Array.isArray(event && event.size) ? event.size : [],
          lineItemId: event && event.lineItemId,
          creativeId: event && event.creativeId
        });
        const hasPrebidWinner = getSlotTargeting(slot, 'hb_adid').length > 0
          || getSlotTargeting(slot, 'hb_pb').length > 0;
        if (hasPrebidWinner) {
          diagnostics.record('prebid_gam_conversion', {
            slotId,
            gamResponded: !(event && event.isEmpty),
            lineItemId: event && event.lineItemId,
            creativeId: event && event.creativeId
          });
        }
        // Brand lift exposure (lib/insights/study.js): `campaignId` is the
        // GAM order, matched against the reader's live studies.
        recordStudySlotRender(options.brand, {
          slotId,
          campaignId: event && event.campaignId,
          lineItemId: event && event.lineItemId,
          isEmpty: !!(event && event.isEmpty)
        });
      };
      impressionViewableHandler = (event) => {
        const slot = event && event.slot;
        recordStudySlotViewable(
          options.brand,
          slot && typeof slot.getSlotElementId === 'function' ? slot.getSlotElementId() : ''
        );
      };
      pubads.addEventListener('slotRenderEnded', slotRenderHandler);
      pubads.addEventListener('impressionViewable', impressionViewableHandler);
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (coordinator) coordinator.destroyAll();
      if (pubads && slotRenderHandler && typeof pubads.removeEventListener === 'function') {
        pubads.removeEventListener('slotRenderEnded', slotRenderHandler);
        pubads.removeEventListener('impressionViewable', impressionViewableHandler);
      }
      if (options.pbjs && typeof options.pbjs.offEvent === 'function') {
        prebidHandlers.forEach(([eventType, handler]) => {
          options.pbjs.offEvent(eventType, handler);
        });
      }
      cleanupHandlers.splice(0).forEach((handler) => {
        try {
          handler();
        } catch (_) {
          // Runtime DOM cleanup is best-effort during reinitialization.
        }
      });
      diagnostics.destroy();
      if (runtimeWindow && runtimeWindow._millaAdRuntime === runtime) {
        delete runtimeWindow._millaAdRuntime;
      }
    }
  };

  if (
    diagnostics.enabled
    && options.pbjs
    && typeof options.pbjs.onEvent === 'function'
  ) {
    const eventTypes = [
      'auctionInit',
      'bidRequested',
      'bidResponse',
      'noBid',
      'bidTimeout',
      'auctionEnd'
    ];
    eventTypes.forEach((eventType) => {
      const handler = (payload) => {
        const bid = Array.isArray(payload) ? payload[0] : payload;
        diagnostics.record(`prebid_${eventType}`, {
          auctionId: bid && (bid.auctionId || bid.auctionIdStr),
          bidder: bid && (bid.bidderCode || bid.bidder),
          slotId: bid && bid.adUnitCode,
          cpm: bid && bid.cpm,
          currency: bid && bid.currency,
          source: bid && bid.source,
          timedOutBidCount: Array.isArray(payload) ? payload.length : undefined
        });
      };
      options.pbjs.onEvent(eventType, handler);
      prebidHandlers.push([eventType, handler]);
    });
  }

  if (runtimeWindow) {
    runtimeWindow._millaAdRuntime = runtime;
  }
  // One runtime per page (or SPA page): the brand lift pageview count.
  recordStudyPageview(options.brand);
  diagnostics.record('runtime_created', { auctionStrategy: runtime.auctionStrategy });
  return runtime;
}

module.exports = {
  createPageAdRuntime,
  resolveAuctionStrategy
};
